import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useRouter } from 'next/router';
import debounce from 'lodash.debounce';
import { fuseSearch } from 'api/searchIndex';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { ChoiceBadge } from 'components/common/ChoiceBadge/ChoiceBadge';
import { getLocale } from 'i18n/locale';
import { ELocale } from 'i18n/localeEnum';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import TextInput from 'components/blocks/Input/TextInput';
import SearchResultsPopUp from 'components/common/RateResortSearch/SearchResultsPopUp';
import { Routes } from 'routes';
import { REVIEW_ANCHOR } from 'page-components/resort/constants';

import { useSanitizedHtmlProps } from 'util/sanitized';
import styles from './styles.module.scss';

interface IRateResortSearchProps {
  regionTitle: string;
  isWidget?:boolean;
}

const RateResortSearch = ({ regionTitle, isWidget = false }: IRateResortSearchProps) => {
  const router = useRouter();
  const locale = getLocale() as ELocale;
  const { t } = useOTSTranslation('common');
  const [searchResults, setSearchResults] = useState<any>(undefined);
  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(!!searchResults);
  const searchInputBlockRef = useRef<HTMLDivElement>(null);
  const hash = router.asPath.split('#')[1];

  const onChange = useCallback(debounce(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const { results } = await fuseSearch(value, 35, t);

    if (!value && results.resorts.length === 0) {
      setIsPopupVisible(false);
      setSearchResults(undefined);
      return;
    }

    setSearchResults(results.resorts);
    setIsPopupVisible(true);
  }, 300), []);

  const onItemSelect = useCallback((item: any) => {
    if (isWidget && window) {
      window.open(`${Routes.resortTripPlanningReviews(item.resort)}#${REVIEW_ANCHOR}`, '_blank');
    } else {
      router.push(`${Routes.resortTripPlanningReviews(item.resort)}#${REVIEW_ANCHOR}`);
    }
  }, []);

  const handlePopUpStateBasedOnEventParent = useCallback((event: MouseEvent) => {
    if (event.target && event.target instanceof Node) {
      const isClickedInsideSearch = searchInputBlockRef.current && searchInputBlockRef.current.contains(event.target);

      if (!isClickedInsideSearch && isPopupVisible) {
        setIsPopupVisible(false);
      }

      if (isClickedInsideSearch && searchResults) {
        setIsPopupVisible(true);
      }
    }
  }, [searchResults, isPopupVisible]);

  useEffect(() => {
    window.addEventListener('click', handlePopUpStateBasedOnEventParent);

    return () => {
      window.removeEventListener('click', handlePopUpStateBasedOnEventParent);
    };
  }, [searchResults, handlePopUpStateBasedOnEventParent]);

  useEffect(() => {
    const onHashChanged = () => {
      searchInputBlockRef.current?.scrollIntoView();
    };

    window.addEventListener('hashchange', onHashChanged);
    return () => {
      window.removeEventListener('hashchange', onHashChanged);
    };
  }, []);

  useEffect(() => {
    if (hash && hash === REVIEW_ANCHOR) {
      setTimeout(() => {
        searchInputBlockRef.current?.scrollIntoView();
      }, 500);
    }
  }, [hash]);

  const resortReviewHelptext = useSanitizedHtmlProps(t('guide.home.resortReviewHelptext'));

  return (
    <BoxWithShadow>
      <div
        className={styles.rateResortSearchContainer}
        ref={searchInputBlockRef}
      >
        {!isWidget && (
        <div className={styles.titleBlock}>
          <span className={styles.badgeBlock}>
            <ChoiceBadge locale={locale} />
          </span>
          <h2 className={styles.title}>{t('guide.home.rateResortLabel', { region: regionTitle })}</h2>
        </div>
        )}
        <div className={styles.searchBlock}>
          <TextInput onChange={onChange} placeholder={t('homePage.searchBarHelpText')} />
          {isPopupVisible && (
            <SearchResultsPopUp
              label="resorts"
              dataKey="title"
              data={searchResults}
              onSelect={onItemSelect}
            />
          )}
        </div>
      </div>
      {!isWidget && <p className={styles.helpText} {...resortReviewHelptext} />}
    </BoxWithShadow>
  );
};

export default RateResortSearch;
